import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { KeyRound, Plus, Trash2, Copy, Check, AlertTriangle } from 'lucide-react';
import { apiTokenService } from '../services/apiTokenService';
import { useUser } from '../context/UserContext';

export default function ApiTokens() {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [tokenToRevoke, setTokenToRevoke] = useState(null);
  const [newTokenValue, setNewTokenValue] = useState(null);
  const [copied, setCopied] = useState(false);
  const { activeJiraAccount } = useUser();
  const [form, setForm] = useState({ name: '' });

  const loadTokens = useCallback(async () => {
    try {
      const data = await apiTokenService.listTokens();
      setTokens(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTokens();
  }, [loadTokens]);

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error('Please enter a token name');
      return;
    }
    if (!activeJiraAccount?.id) {
      toast.error('No active Jira account. Please connect one in Settings first.');
      return;
    }
    setCreating(true);
    try {
      const result = await apiTokenService.createToken({
        name: form.name.trim(),
        jiraAccountId: activeJiraAccount?.id,
      });
      setNewTokenValue(result.token);
      setShowCreateModal(false);
      await loadTokens();
      toast.success('Token created — copy it now, you won\'t see it again!');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeConfirmed = async () => {
    if (!tokenToRevoke) return;
    const token = tokenToRevoke;
    setRevoking(token.id);
    try {
      await apiTokenService.revokeToken(token.id);
      setTokens((prev) => prev.filter((t) => t.id !== token.id));
      toast.success('Token revoked');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRevoking(null);
      setTokenToRevoke(null);
    }
  };

  const handleCopy = async () => {
    if (!newTokenValue) return;
    await navigator.clipboard.writeText(newTokenValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="p-6 max-w-3xl">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <KeyRound className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">API Tokens</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Use tokens to access Logio from Claude Code or scripts.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Generate Token
        </button>
      </div>

      {/* New token reveal banner */}
      {newTokenValue && (
        <div className="mb-6 p-4 rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700">
          <div className="flex items-start gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                Copy your token now — it won't be shown again
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                Store it somewhere safe like a password manager.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 rounded-lg bg-white dark:bg-slate-900 border border-amber-200 dark:border-slate-700 px-3 py-2 text-sm font-mono text-slate-900 dark:text-white break-all">
              {newTokenValue}
            </code>
            <button
              type="button"
              onClick={handleCopy}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-amber-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm font-medium text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-slate-700 transition-colors"
            >
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <button
            type="button"
            onClick={() => setNewTokenValue(null)}
            className="mt-3 text-xs text-amber-700 dark:text-amber-400 underline"
          >
            I've saved it, dismiss this
          </button>
        </div>
      )}

      {/* Token list */}
      {loading ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading tokens...</p>
      ) : tokens.length === 0 ? (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-8 text-center">
          <KeyRound className="w-10 h-10 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
          <p className="text-sm text-slate-500 dark:text-slate-400">
            No tokens yet. Generate one to get started.
          </p>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Token</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Jira Account</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Last Used</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Created</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {tokens.map((token) => (
                <tr
                  key={token.id}
                  className="bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                >
                  <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{token.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500 dark:text-slate-400">{token.token_prefix}</td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{token.jira_account_name}</td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400">
                    {token.last_used_at ? new Date(token.last_used_at).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400">
                    {new Date(token.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => setTokenToRevoke(token)}
                      disabled={revoking === token.id}
                      className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30 border border-rose-200 dark:border-rose-800 transition-colors disabled:opacity-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      {revoking === token.id ? 'Revoking...' : 'Revoke'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-2xl bg-white dark:bg-slate-800 p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Generate API Token</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Token Name
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Claude Code — my laptop"
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="rounded-lg border border-slate-300 dark:border-slate-600 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={creating}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {creating ? 'Generating...' : 'Generate Token'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke confirm modal */}
      {tokenToRevoke && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-2xl bg-white dark:bg-slate-800 p-6 shadow-xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="rounded-full bg-rose-100 dark:bg-rose-900/30 p-2 flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-rose-600 dark:text-rose-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Revoke API Token</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                  Revoke token <span className="font-semibold">"{tokenToRevoke.name}"</span>? Any
                  scripts or tools using it will stop working. This cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setTokenToRevoke(null)}
                disabled={revoking === tokenToRevoke.id}
                className="rounded-lg border border-slate-300 dark:border-slate-600 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRevokeConfirmed}
                disabled={revoking === tokenToRevoke.id}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50 transition-colors"
              >
                {revoking === tokenToRevoke.id ? 'Revoking...' : 'Revoke Token'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}